import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { RBAC_BASE } from '../../../api/rbacApi';

const RoleAssign = () => {
	const { userId } = useParams();
	const navigate = useNavigate();

	const [users, setUsers] = useState([]);
	const [roles, setRoles] = useState([]);
	const [selectedUser, setSelectedUser] = useState('');
	const [selectedRoles, setSelectedRoles] = useState([]);
	const [loading, setLoading] = useState(false);

	const load = useCallback(async () => {
		try {
			const [u, r] = await Promise.all([
				api.get(`${RBAC_BASE}/user-role-mapping`),
				api.get(`${RBAC_BASE}/roles`)
			]);

			setUsers(u.data);
			setRoles(r.data);

			if (userId) {
				const user = u.data.find(x => String(x.intUserId) === String(userId));
				if (user) {
					setSelectedUser(user.intUserId);
					setSelectedRoles([
						...new Set([
							user.intDesigId,
							...(user.extraRoles || [])
						].filter(Boolean))
					]);
				}
			}

		} catch (err) {
			console.error(err);
		}
	}, [userId]);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		if (userId) {
			setSelectedUser(userId);
		}
	}, [userId]);

	const cleanedRoles = selectedRoles.filter(
		role => role !== null &&
			role !== undefined &&
			role !== ""
	);

	const handleSave = async () => {
		if (!selectedUser) {
			alert("Please select user");
			return;
		}

		try {
			setLoading(true);

			await api.post(`${RBAC_BASE}/assign-role`, {
				userId: selectedUser,
				roleIds: cleanedRoles
			});

			alert("Role assigned successfully");

			navigate('/role-access-list');

		} catch (err) {
			console.error(err);
			alert("Error assigning role");
		} finally {
			setLoading(false);
		}
	};

	const toggleRole = (roleId) => {
		setSelectedRoles(prev =>
			prev.includes(roleId)
				? prev.filter(r => r !== roleId)
				: [...prev, roleId]
		);
	};

	return (
		<div className="container-fluid admin-module-page admin-role-assign-page py-4">
			<div className="card admin-surface-card admin-filter-card sticky-top mb-4">
				<div className="card-body admin-surface-body d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
					<div>
						<span className="admin-page-kicker">Access Control</span>
						<h3 className="admin-page-title fw-bold mb-1">Assign Role to User</h3>
						<p className="admin-page-description mb-0">
							Select a user, review the primary role, and assign any additional roles.
						</p>
					</div>

					<button
						className="btn btn-primary admin-page-action"
						onClick={handleSave}
						disabled={loading}
					>
						{loading ? "Saving..." : "Save"}
					</button>
				</div>
			</div>

			{selectedUser && (
				<div className="alert alert-light border admin-surface-card mb-4">
					User: <strong>{users.find(u => String(u.intUserId) === String(selectedUser))?.vchFullName}</strong>
				</div>
			)}

			<div className="row g-4">
				<div className="col-lg-4">
					<div className="card admin-surface-card h-100">
						<div className="card-header admin-surface-header fw-bold">Users</div>
						<div className="card-body admin-surface-body">
							<div style={{ maxHeight: "520px", overflowY: "auto" }}>
								{users.map(u => (
									<div
										key={u.intUserId}
										className={`admin-tree-item p-3 mb-2 ${String(selectedUser) === String(u.intUserId) ? "bg-primary text-white" : "bg-light"}`}
										onClick={() => {
											setSelectedUser(u.intUserId);
											setSelectedRoles([
												...new Set([
													u.intDesigId,
													...(u.extraRoles || [])
												].filter(Boolean))
											]);
										}}
										role="button"
										tabIndex={0}
									>
										<div className="fw-semibold">{u.vchFullName}</div>
										<small className={String(selectedUser) === String(u.intUserId) ? "text-white-50" : "text-muted"}>
											{u.vchEmail}
										</small>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>

				<div className="col-lg-8">
					<div className="card admin-surface-card h-100">
						<div className="card-header admin-surface-header fw-bold">Assign Role</div>
						<div className="card-body admin-surface-body">
							{!selectedUser ? (
								<div className="text-muted">Select a user to assign role</div>
							) : (
								<div className="d-flex flex-wrap gap-2">
									{roles.map(r => {
										const primaryRole = users.find(u => String(u.intUserId) === String(selectedUser))?.intDesigId;
										const isPrimary = String(r.intDesigId) === String(primaryRole);
										const isSelected = selectedRoles.includes(r.intDesigId);

										return (
											<div
												key={r.intDesigId}
												onClick={() => !isPrimary && toggleRole(r.intDesigId)}
												className={`admin-chip ${isPrimary
													? "bg-dark text-white border-dark"
													: isSelected
														? "bg-primary-subtle text-primary border-primary"
														: "bg-light border-light"
													}`}
												style={{
													cursor: isPrimary ? "not-allowed" : "pointer",
													opacity: isPrimary ? 0.8 : 1
												}}
											>
												{r.nvchDesigName}
												{isPrimary && <span className="ms-1">(Primary)</span>}
											</div>
										);
									})}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default RoleAssign;